import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AdvancedSetting } from '../types/AdvancedSetting';
import AdvancedService from '../services/advancedService';
import { UtilsService } from '../services/utilsService';
import { ToastType, useToast } from '../pages/toast/ToastProvider';

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (settingsObj: AdvancedSetting) => void;
}

const AdvancedSettingsModal: React.FC<Props> = ({ show, onClose, onSave }) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const isRtl = i18n.language === 'he';
  const activeClinicId = sessionStorage.getItem('activeClinicId') || '';
  const [originalSettings, setOriginalSettings] = useState<AdvancedSetting | null>(null);

  const [advancedSetting, setAdvancedSetting] = useState<AdvancedSetting>({
    clinic: {
      clinicId: activeClinicId,
      clinicPhone: '',
      clinicNewAppointmentAlert: false,
    },
    ui: {
      darkMode: false,
    },
  });

  // Load settings from server when modal opens
  useEffect(() => {
    if (!show || !activeClinicId) return;

    const fetchSettings = async () => {
      try {
        const res = await AdvancedService.getByClinicId(activeClinicId);
        if (res.data) {
          setAdvancedSetting(res.data);     // form state
          setOriginalSettings(res.data);    // for change comparison
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          console.error('Failed to load advanced settings:', err);
        }
      }
    };

    fetchSettings();
  }, [show, activeClinicId]);

  // Also load from props if passed
  useEffect(() => {
    if (originalSettings) {
      setAdvancedSetting(originalSettings);
    }
  }, [originalSettings]);


  const handleClinicPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdvancedSetting(prev => ({
      ...prev,
      clinic: {
        ...prev.clinic,
        clinicPhone: e.target.value,
      },
    }));
  };

  const handleClinicAlertToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdvancedSetting(prev => ({
      ...prev,
      clinic: {
        ...prev.clinic,
        clinicNewAppointmentAlert: e.target.checked,
      },
    }));
  };

  const hasChanges = (original: AdvancedSetting | null, current: AdvancedSetting) => {
    if (!original) return true; // no original, consider as changed

    return (
      original.clinic.clinicPhone !== current.clinic.clinicPhone ||
      original.clinic.clinicNewAppointmentAlert !== current.clinic.clinicNewAppointmentAlert ||
      original.ui.darkMode !== current.ui.darkMode
    );
  };

  const handleSaveSettings = () => {
    if (!UtilsService.validatePhoneNumber(advancedSetting?.clinic?.clinicPhone)) {
      showToast(t(`toastMessages.FILL_VALID_PHONE`), ToastType.Warning);
      return;
    }

    const normalizedPhone = UtilsService.normalizeIsraeliPhoneNumber(
      advancedSetting?.clinic?.clinicPhone || ''
    );

    if (!normalizedPhone) {
      showToast(t(`toastMessages.INVALID_PHONE_FORMAT`), ToastType.Warning);
      return;
    }

    const updatedSettings = {
      ...advancedSetting,
      clinic: {
        ...advancedSetting.clinic,
        clinicPhone: normalizedPhone,
      },
    };

    if (!hasChanges(originalSettings, updatedSettings)) {
      showToast(t(`toastMessages.NO_CHANGES_TO_SAVE`), ToastType.Success);
      return;
    }

    onSave(updatedSettings);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered backdrop="static" className="text-light" contentClassName="bg-dark">
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>{t('advanced-settings')}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4">
        {/* Clinic Section */}
        <h5 className="mb-3">{t('clinic')}</h5>
        <Row className="mb-4">
          <Col xs={12} md={4} className="mb-3 mb-md-0">
            <Form.Label className="text-secondary">{t('clinic-phone-title')}</Form.Label>
            <InputGroup>
              <Form.Control
                type="tel"
                placeholder={t('enter-phone')}
                className={`bg-secondary bg-opacity-25 text-light form-control ${isRtl ? 'ms-2' : 'me-2'}`}
                value={advancedSetting.clinic.clinicPhone}
                onChange={handleClinicPhoneChange}
              />
            </InputGroup>
          </Col>

          <Col xs={12} md={4} className="d-flex align-items-center">
            <Form.Check
              type="checkbox"
              id="notify-clinic-checkbox"
              className={`mt-2 text-gray d-flex align-items-center align-self-end ${isRtl ? 'me-2' : 'ms-2'}`}
              label={<span className="text-gray" style={{ verticalAlign: 'sub' }}>{t('notify-clinic-on-new-appointment')}</span>}
              checked={advancedSetting.clinic.clinicNewAppointmentAlert}
              onChange={handleClinicAlertToggle}
            />
          </Col>
        </Row>

        {/* Another Section */}
        
        
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={handleSaveSettings}>
          {t('save-changes')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdvancedSettingsModal;
